import React from 'react';
import useReactRouter from 'use-react-router';
import { SITE_URL } from 'env';
import { useGenerateSharedUrlMutation, useFinishUserLivenessOnboardingMutation } from 'generated/graphql';
import { isIosDevice, isSafariBrowser } from 'utils/utils';
import { RouteParams } from 'components/custom-route/custom-route';
import { BizonScreenWrapper } from 'components/commons';
import LoadingFallback from './loading-fallback';
import ModalSafariRedirect from './components/safari-redirect-modal'
import { useAntiFraudConsumer } from './hooks';
import { GenericErrorLayout } from '../direto/error-message';
import './bizon-screen.styles.scss';

type LivenessIdentityProps = {
    onComplete(): void,
};

const LivenessIdentity: React.FC<LivenessIdentityProps> = ({ onComplete }): JSX.Element => {
    const [sharedUrl, setSharedUrl] = React.useState('');
    const [isError, setIsError] = React.useState(false);
    const { match: { params: { loanApplicationId } } } = useReactRouter<RouteParams>();

    const { refetch } = useAntiFraudConsumer();
    const [generateSharedUrlHook] = useGenerateSharedUrlMutation();
    const [finishUserLivenessOnboardingHook] = useFinishUserLivenessOnboardingMutation();

    const eventHandler = async (event: MessageEvent): Promise<void> => {
        const urlObj = new URL(sharedUrl);
        if (event.origin.startsWith(`${urlObj.protocol}//${urlObj.hostname}`)
            && event.data.code === "ONBOARDING_FINISHED") {
            await finishUserLivenessOnboardingHook({
                variables: {
                    loanApplicationId,
                },
            });
            await refetch();

            onComplete();
        }
    }

    React.useEffect(() => {
        const generateSharedUrlAsync = async () => {
            try {
                const { data } = await generateSharedUrlHook({
                    variables: {
                        loanApplicationId
                    }
                });
                setSharedUrl(data?.generateSharedUrl.url);
            } catch (error) {
                setIsError(true);
            }
        };
        generateSharedUrlAsync();

        return () => {
            window.removeEventListener('message', eventHandler);
        }
    }, []);

    React.useEffect(() => {
        if (sharedUrl) {
            window.removeEventListener('message', eventHandler);
            window.addEventListener('message', eventHandler);
        }
    }, [sharedUrl]);

    if (isError) {
        return (
            <BizonScreenWrapper>
                <div className="bizon-screen grid-container">
                    <div className="bizon-screen__content">
                        <GenericErrorLayout />
                    </div>
                </div>
            </BizonScreenWrapper>
        );
    }

    if (isIosDevice() && !isSafariBrowser()) {
        return <ModalSafariRedirect url={`${SITE_URL}/pedido/${loanApplicationId}`} />
    }

    return (
        <LoadingFallback loading={!sharedUrl}>
            <iframe
                src={sharedUrl}
                allow={`camera;fullscreen;accelerometer;gyroscope;magnetometer; ${sharedUrl};`}
                className="bizon-liveness-iframe"
            >
                <p className="text-center">Houve um problema no carregamento da página</p>
            </iframe>
        </LoadingFallback>
    );
};

export default LivenessIdentity;
